import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Flex,
  Spinner,
  Stack,
  Text,
  Textarea,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  UnifiedEmptyState,
  UnifiedSurfaceCard,
} from 'components/layout/UnifiedPrimitives';
import {
  getListingMessages,
  markMessageRead,
  sendListingMessage,
} from 'domains/collaboration/services/collaborationService';
import { consumeMessageDraft } from 'domains/ai/utils/storage';
import { useTranslation } from 'i18n/TranslationProvider';

const POLL_INTERVAL_MS = 10000;

function MessageBubble({ message, isOwn }) {
  const { t } = useTranslation();

  const ownBg = useColorModeValue('brand.50', 'brand.900');
  const otherBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const titleColor = useColorModeValue('navy.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const translatedColor = useColorModeValue('blue.500', 'blue.300');

  const displayText = message.translatedText || message.text;
  const hasTranslation = Boolean(message.translatedText);

  const time = message.createdAt
    ? new Date(message.createdAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  return (
    <Flex justify={isOwn ? 'flex-end' : 'flex-start'} w="100%">
      <Box
        maxW="75%"
        bg={isOwn ? ownBg : otherBg}
        borderRadius="18px"
        px={4}
        py={3}
        border="1px solid"
        borderColor={useColorModeValue(
          isOwn ? 'brand.100' : 'gray.100',
          isOwn ? 'brand.700' : 'whiteAlpha.200',
        )}
      >
        <Flex align="start" mb={2}>
          <Avatar size="xs" name={message.senderName || message.senderRole} mr={2} mt="2px" />
          <Box flex="1">
            <Text
              fontSize="sm"
              fontWeight="700"
              color={titleColor}
              lineHeight="shorter"
            >
              {message.senderName || message.senderRole}
            </Text>
            {message.senderName && (
              <Text
                fontSize="10px"
                fontWeight="800"
                textTransform="uppercase"
                letterSpacing="0.08em"
                color={mutedColor}
                lineHeight="shorter"
              >
                {message.senderRole}
              </Text>
            )}
          </Box>
          <Text fontSize="10px" color={mutedColor} ml={2} mt="2px">
            {time}
          </Text>
        </Flex>
        <Text
          fontSize="sm"
          fontWeight="600"
          color={titleColor}
          whiteSpace="pre-wrap"
        >
          {displayText}
        </Text>
        {hasTranslation ? (
          <Text fontSize="10px" fontWeight="700" color={translatedColor} mt={1}>
            {t('translated')}
          </Text>
        ) : null}
      </Box>
    </Flex>
  );
}

export default function ListingMessagesPanel({
  listingId,
  currentUserId,
  role,
  onUnreadCountChange,
}) {
  const { t } = useTranslation();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(() => {
    if (role && listingId) {
      return consumeMessageDraft(role, listingId) || '';
    }
    return '';
  });
  const scrollRef = useRef(null);
  const markingRef = useRef(false);
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const titleColor = useColorModeValue('navy.800', 'white');

  const fetchMessages = useCallback(async () => {
    if (!listingId) return;
    try {
      const data = await getListingMessages(listingId);
      setMessages(data);
    } catch (err) {
      console.error('[ListingMessagesPanel] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [listingId]);
  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  // Mark unread messages from other users as read when panel is visible
  useEffect(() => {
    if (!currentUserId || markingRef.current) return;

    const unread = messages.filter(
      (msg) => msg.senderId !== currentUserId && !msg.read,
    );

    if (unread.length === 0) {
      if (onUnreadCountChange) onUnreadCountChange(0);
      return;
    }

    markingRef.current = true;
    Promise.all(unread.map((msg) => markMessageRead(msg.id)))
      .then(() => {
        if (onUnreadCountChange) onUnreadCountChange(0);
        return fetchMessages();
      })
      .catch((err) => {
        console.warn('[ListingMessagesPanel] mark-read error:', err);
      })
      .finally(() => {
        markingRef.current = false;
      });
  }, [messages, currentUserId, onUnreadCountChange, fetchMessages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;

    setSending(true);
    try {
      await sendListingMessage(listingId, text);
      setDraft('');
      await fetchMessages();
    } catch (err) {
      console.error('[ListingMessagesPanel] send error:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return (
      <UnifiedSurfaceCard p={6}>
        <Flex align="center" justify="center" minH="200px">
          <Spinner size="md" color="brand.500" />
        </Flex>
      </UnifiedSurfaceCard>
    );
  }

  return (
    <UnifiedSurfaceCard p={5}>
      <Box>
        <Text
          fontSize="10px"
          fontWeight="800"
          color={mutedColor}
          textTransform="uppercase"
          letterSpacing="0.08em"
        >
          {t('messages')}
        </Text>
        <Text fontSize="lg" fontWeight="900" color={titleColor}>
          {t('directCommunication')}
        </Text>
        <Text fontSize="sm" color={mutedColor} mt={1}>
          {t('discussTransportDetails')}
        </Text>
      </Box>

      {messages.length === 0 ? (
        <Box mt={5}>
          <UnifiedEmptyState
            title={t('noMessagesYet')}
            description={t('startConversation')}
            minH="140px"
          />
        </Box>
      ) : (
        <Box
          ref={scrollRef}
          mt={5}
          maxH="400px"
          overflowY="auto"
          px={1}
          css={{
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: '#CBD5E0',
              borderRadius: '3px',
            },
          }}
        >
          <Stack spacing={3}>
            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                isOwn={msg.senderId === currentUserId}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Box mt={5}>
        <Textarea
          placeholder={t('typeMessage')}
          size="sm"
          borderRadius="lg"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          resize="vertical"
          minH="80px"
        />
        <Flex justify="flex-end" mt={3}>
          <Button
            size="sm"
            colorScheme="blue"
            borderRadius="lg"
            onClick={handleSend}
            isLoading={sending}
            isDisabled={!draft.trim()}
          >
            {t('sendMessage')}
          </Button>
        </Flex>
      </Box>
    </UnifiedSurfaceCard>
  );
}
